// @owner ClientPlatform
/* eslint-disable jsx-a11y/label-has-associated-control */

import type {FC, PropsWithChildren} from 'react';
import React, {useState} from 'react';
import {SigninPage, type SwitchToSigninPage} from '../../helpers/pageHelpers';
import {AuthCard} from '../../components/authCard/authCard';
import {Input} from '../../../common/components/input';
import {FullWidthButton} from '../../../common/components/button';
import {Text} from '../../../common/components/text';
import {requester} from '../../../helpers/requestHelpers';
import {StyledFieldsGroup} from '../../commonSigninStyles';

interface ForgotPasswordProps {
  switchToSigninPage: SwitchToSigninPage;
  setForgotEmail: (email: string) => void;
}

interface ForgotPasswordWindow extends Window {
  analytics?: {
    identify: (email: string) => void;
    track: (eventName: string) => void;
  };
}

function findForgotPasswordWindow() {
  return window as ForgotPasswordWindow;
}

export const ForgotPassword: FC<PropsWithChildren<ForgotPasswordProps>> = ({switchToSigninPage, setForgotEmail}) => {
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);

  async function requestNewPassword() {
    setIsSending(true);
    setError('');

    setForgotEmail(email);

    try {
      await requester.post(`/api/1/request_new_password/${email}`);

      const window = findForgotPasswordWindow();
      if (window.analytics) {
        window.analytics.identify(email);
        window.analytics.track('clicked_reset_password_app');
      }

      switchToSigninPage(SigninPage.PASSWORD_RESENT);
    } catch (e) {
      if (e.response.status === 404) {
        setError('reset_password_not_found');
      }
    } finally {
      setIsSending(false);
    }
  }

  return (
    <AuthCard
      name="forgotPasswordform"
      onSubmit={(e) => {
        e.preventDefault();
        requestNewPassword();
      }}
    >
      <Text as="h1" variant="title-small-semibold">
        Reset password
      </Text>
      <StyledFieldsGroup>
        <Input
          value={email}
          id="email-login"
          label="Email"
          type="email"
          name="email"
          placeholder="Email address"
          required
          autoFocus
          onChange={setEmail}
          error={error === 'reset_password_not_found' ? 'Email not found' : undefined}
        />
        <FullWidthButton type="submit" disabled={isSending}>
          Reset my password
        </FullWidthButton>
        <FullWidthButton type="button" variant="tertiary" onClick={() => switchToSigninPage(SigninPage.LOGIN)}>
          Back to login
        </FullWidthButton>
      </StyledFieldsGroup>
    </AuthCard>
  );
};
